(function (root) {
  'use strict';
  function text(value) { return typeof value === 'string' && value.trim() !== ''; }
  function settings(value) {
    if (!value || !['clubName', 'welcomeMessage', 'badgePattern', 'milestoneMessage'].every(key => text(value[key])) ||
      ![value.startsAt, value.interval].every(n => Number.isSafeInteger(n) && n > 0) || !value.badgePattern.includes('{count}')) {
      throw new Error('Invalid milestone settings.');
    }
    return value;
  }
  const letters = {'ø': 'o', 'æ': 'ae', 'œ': 'oe', 'ß': 'ss', 'ł': 'l', 'đ': 'd', 'ð': 'd', 'þ': 'th'};
  function fold(value) {
    // Accent-insensitive matching: "rene" finds "René" and "soren" finds "Søren".
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[øæœßłđðþ]/g, c => letters[c]);
  }
  function label(pattern, count, config) {
    return pattern.replace(/\{club\}/g, config.clubName).replace(/\{count\}/g, String(count));
  }
  function compareRetreats(a, b) {
    // The chronological Retreats catalog resolves editions within the same year.
    return (a?.year ?? Infinity) - (b?.year ?? Infinity) ||
      (a?.order ?? Infinity) - (b?.order ?? Infinity) || 0;
  }
  function milestone(total, config) {
    settings(config);
    if (!Number.isSafeInteger(total) || total < 0) throw new Error('Invalid retreat count.');
    const club = total >= config.startsAt;
    const earned = club ? config.startsAt + Math.floor((total - config.startsAt) / config.interval) * config.interval : 0;
    const next = club ? earned + config.interval : config.startsAt;
    const badges = [];
    for (let count = config.startsAt; count <= total; count += config.interval) {
      badges.push({id: 'club-' + count, count, name: count === config.startsAt ? config.clubName : label(config.badgePattern, count, config),
        message: label(count === config.startsAt ? config.welcomeMessage : config.milestoneMessage, count, config)});
    }
    return {club, earned, next, remaining: next - total, percent: total / next * 100, badges, badge: badges.length ? badges[badges.length - 1].name : ''};
  }
  function normalize(payload) {
    if (!payload || payload.schemaVersion !== 2 || payload.source !== 'grown-strong-passport' ||
      !Array.isArray(payload.participants) || !Array.isArray(payload.destinations) || !Array.isArray(payload.events)) {
      throw new Error('This passport data format is not supported.');
    }
    const config = settings(payload.milestones);
    const completedRetreats = payload.communityStats?.completedRetreats ?? null;
    if (completedRetreats !== null && (!Number.isSafeInteger(completedRetreats) || completedRetreats < 0)) {
      throw new Error('Invalid completed retreat total.');
    }
    const destinations = payload.destinations.map(d => {
      if (!text(d.id) || !text(d.label) || typeof d.country !== 'string') throw new Error('Invalid destination.');
      return {id: d.id, label: d.label, country: d.country};
    });
    const byPlace = new Map(destinations.map(d => [d.id, d]));
    if (byPlace.size !== destinations.length) throw new Error('Duplicate destination IDs.');
    const events = new Map();
    payload.events.forEach((e, order) => {
      if (!text(e.id) || events.has(e.id) || !text(e.name) || !byPlace.has(e.destinationId) ||
        !['Full retreat', 'Reunion'].includes(e.tripType) || (e.tripType === 'Reunion' && !text(e.badgeName))) throw new Error('Invalid trip.');
      const place = byPlace.get(e.destinationId);
      const edition = e.name.match(/(?:^|·)\s*((?:19|20)\d{2})(?:\s*·\s*(.*))?\s*$/);
      events.set(e.id, {id: e.id, name: e.name, destinationId: e.destinationId, tripType: e.tripType, badgeName: e.badgeName,
        label: place.label, country: place.country, year: edition ? Number(edition[1]) : null,
        edition: edition && edition[2] ? edition[2].trim() : '', order});
    });
    const ids = new Set();
    const bookingIssues = [];
    function participant(p, order) {
      if (!p || !text(p.id) || ids.has(p.id) || !text(p.name)) throw new Error('Invalid participant.');
      const used = new Set();
      function history(eventIds, tripType) {
        if (!Array.isArray(eventIds)) throw new Error('Missing trip history.');
        return eventIds.map(id => {
          const event = events.get(id);
          if (!event || event.tripType !== tripType || used.has(id)) throw new Error('Invalid or duplicate trip attendance.');
          used.add(id);
          return event;
        });
      }
      const retreats = history(p.retreatIds, 'Full retreat').sort(compareRetreats);
      const reunions = history(p.reunionIds, 'Reunion');
      const bookedIds = p.bookedRetreatIds === undefined ? [] : p.bookedRetreatIds;
      const upcoming = [];
      let upcomingIncomplete = false;
      function bookingIssue(eventId, reason) {
        upcomingIncomplete = true;
        bookingIssues.push({id: p.id, eventId: typeof eventId === 'string' ? eventId : null, reason});
      }
      if (!Array.isArray(bookedIds)) bookingIssue(null, 'Invalid booked retreat list.');
      else bookedIds.forEach(id => {
        const event = events.get(id);
        if (!event || event.tripType !== 'Full retreat' || used.has(id)) {
          bookingIssue(id, 'Invalid or duplicate booked retreat.');
          return;
        }
        used.add(id);
        upcoming.push(event);
      });
      upcoming.sort(compareRetreats);
      const counts = new Map();
      retreats.forEach(event => counts.set(event.destinationId, (counts.get(event.destinationId) || 0) + 1));
      const trips = [...counts].map(([id, count]) => ({...byPlace.get(id), count}));
      const total = retreats.length;
      const progress = milestone(total, config);
      ids.add(p.id);
      return {id: p.id, name: p.name.trim(), retreats, reunions, upcoming, upcomingIncomplete, trips, total, order, ...progress};
    }
    const participants = payload.participants.filter(p => p?.visibility === 'public').map(participant)
      .sort((a, b) => b.total - a.total || compareRetreats(a.retreats[0], b.retreats[0]) ||
      a.order - b.order || a.id.localeCompare(b.id));
    let previousTotal = null;
    let rank = 0;
    const frequencies = new Map();
    participants.forEach(p => frequencies.set(p.total, (frequencies.get(p.total) || 0) + 1));
    participants.forEach((p, i) => {
      if (p.total !== previousTotal) rank = i + 1;
      p.rank = rank;
      p.tied = frequencies.get(p.total) > 1;
      previousTotal = p.total;
    });
    return {destinations, participants, milestones: config, completedRetreats, bookingIssues};
  }
  function filter(participants, {query = '', destination = '', club = false, reunion = false} = {}) {
    const q = fold(query.trim());
    return participants.filter(p => (!q || fold(p.name).includes(q)) &&
      (!destination || p.trips.some(t => t.id === destination)) && (!club || p.club) && (!reunion || p.reunions.length > 0));
  }
  const api = {normalize, filter, milestone};
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.GSPassportModel = api;
})(typeof window !== 'undefined' ? window : null);
